import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { useUserAuth } from "./UserAuthContext";

const UserLoginPanel = () => {
  const { login, logout, user, token, isAuthenticated } = useUserAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // New-user creation state (admin only)
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");

  // Change-my-password state (any authenticated user)
  const [currentPassword, setCurrentPassword] = useState("");
  const [myNewPassword, setMyNewPassword] = useState("");
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [changeError, setChangeError] = useState("");
  const [changeSuccess, setChangeSuccess] = useState("");

  // Admin reset-another-user-password state
  const [resetEmail, setResetEmail] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");
    setSubmitting(true);

    try {
      const result = await login(email, password);
      setSuccessMessage(`Logged in as ${result.user.email}`);
    } catch (err) {
      console.error("Login failed:", err);
      setError(err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setSuccessMessage("Logged out successfully");
      setEmail("");
      setPassword("");
    } catch (err) {
      console.error("Logout failed:", err);
      setError(err.message || "Logout failed");
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setCreateError("");
    setCreateSuccess("");

    if (!newEmail || !newPassword) {
      setCreateError("Email and password are required");
      return;
    }

    setCreateSubmitting(true);
    try {
      const res = await fetch("/api/auth/users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          email: newEmail,
          password: newPassword,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg = data && data.error ? data.error : "Failed to create user";
        throw new Error(msg);
      }

      setCreateSuccess(
        `User ${data.email || newEmail} created with role ${
          data.role || "standard"
        }.`
      );
      setNewEmail("");
      setNewPassword("");
    } catch (err) {
      console.error("Create user failed:", err);
      setCreateError(err.message || "Failed to create user");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleChangeMyPassword = async (event) => {
    event.preventDefault();
    setChangeError("");
    setChangeSuccess("");

    if (!currentPassword || !myNewPassword) {
      setChangeError("Both current and new password are required");
      return;
    }

    setChangeSubmitting(true);
    try {
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: myNewPassword,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg = data && data.error ? data.error : "Failed to change password";
        throw new Error(msg);
      }

      setChangeSuccess("Password updated successfully.");
      setCurrentPassword("");
      setMyNewPassword("");
    } catch (err) {
      console.error("Change password failed:", err);
      setChangeError(err.message || "Failed to change password");
    } finally {
      setChangeSubmitting(false);
    }
  };

  const handleAdminResetPassword = async (event) => {
    event.preventDefault();
    setResetError("");
    setResetSuccess("");

    if (!resetEmail || !resetPassword) {
      setResetError("User email and new password are required");
      return;
    }

    setResetSubmitting(true);
    try {
      const res = await fetch("/api/auth/admin/reset-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          email: resetEmail,
          new_password: resetPassword,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const msg = data && data.error ? data.error : "Failed to reset password";
        throw new Error(msg);
      }

      setResetSuccess(`Password reset for ${data.email || resetEmail}.`);
      setResetEmail("");
      setResetPassword("");
    } catch (err) {
      console.error("Admin reset password failed:", err);
      setResetError(err.message || "Failed to reset password");
    } finally {
      setResetSubmitting(false);
    }
  };

  const isAdmin = isAuthenticated && user && user.role === "admin";

  return (
    <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
      <Card sx={{ width: 460, maxWidth: "100%" }}>
        <CardHeader title="User Login" />
        <CardContent>
          {isAuthenticated && user && (
            <Alert
              severity="info"
              sx={{ mb: 2, display: "flex", alignItems: "center" }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleLogout}
                  disabled={submitting}
                >
                  LOGOUT
                </Button>
              }
            >
              Currently logged in as <strong>&nbsp;{user.email}</strong>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {/* Login form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />

            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? "Logging in..." : "Login"}
              </Button>
            </Box>
          </Box>

          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              This login uses the backend <code>/api/auth/login</code> endpoint,
              stores auth in a secure cookie, and tracks your sessions.
            </Typography>
          </Box>

          {/* Change my password */}
          {isAuthenticated && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Change My Password
              </Typography>

              {changeError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {changeError}
                </Alert>
              )}

              {changeSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {changeSuccess}
                </Alert>
              )}

              <Box component="form" onSubmit={handleChangeMyPassword}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type="password"
                  margin="normal"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />

                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  margin="normal"
                  value={myNewPassword}
                  onChange={(e) => setMyNewPassword(e.target.value)}
                  autoComplete="new-password"
                />

                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={changeSubmitting}
                  >
                    {changeSubmitting ? "Updating..." : "Update Password"}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Admin: create new user */}
          {isAdmin && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Create New User
              </Typography>

              {createError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {createError}
                </Alert>
              )}

              {createSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {createSuccess}
                </Alert>
              )}

              <Box component="form" onSubmit={handleCreateUser}>
                <TextField
                  fullWidth
                  label="New User Email"
                  type="email"
                  margin="normal"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />

                <TextField
                  fullWidth
                  label="New User Password"
                  type="password"
                  margin="normal"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />

                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={createSubmitting}
                  >
                    {createSubmitting ? "Creating..." : "Create User"}
                  </Button>
                </Box>
              </Box>

              <Box mt={1}>
                <Typography variant="body2" color="textSecondary">
                  New users are created with <strong>standard</strong> role and{" "}
                  <strong>active</strong> status. Only admins can create users.
                </Typography>
              </Box>

              <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  Reset Another User Password
                </Typography>

                {resetError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {resetError}
                  </Alert>
                )}

                {resetSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {resetSuccess}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleAdminResetPassword}>
                  <TextField
                    fullWidth
                    label="User Email"
                    type="email"
                    margin="normal"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                  />

                  <TextField
                    fullWidth
                    label="Temporary / New Password"
                    type="password"
                    margin="normal"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    helperText="Minimum 8 characters."
                  />

                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={resetSubmitting}
                    >
                      {resetSubmitting ? "Resetting..." : "Reset User Password"}
                    </Button>
                  </Box>
                </Box>

                <Box mt={1}>
                  <Typography variant="body2" color="textSecondary">
                    Admins can reset another active user&apos;s password without
                    knowing the user&apos;s current password.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserLoginPanel;
